import React, { useEffect, useState } from "react";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Plus, Shield, Crown, CalendarDays, Radio, Loader2, KeyRound } from "lucide-react";

// Descriptors kept in sync with backend `require_role` matrix.
const ROLE_META = {
  super_admin:  { label: "Super Admin",  icon: Crown,        color: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
                  blurb: "Platform-wide access across every tenant" },
  tenant_admin: { label: "Tenant Admin", icon: Shield,       color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
                  blurb: "Full control inside this tenant — users, billing, channels" },
  scheduler:    { label: "Scheduler",    icon: CalendarDays, color: "bg-amber-500/15 text-amber-300 border-amber-500/40",
                  blurb: "Author schedules, upload media, tag categories, run auto-fill" },
  operator:     { label: "Operator",     icon: Radio,        color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
                  blurb: "On-air playout — start/stop channels, trigger SCTE, manage graphics" },
};

const CREATE_ROLES = ["operator", "scheduler", "tenant_admin"];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "operator" });
  const [creating, setCreating] = useState(false);
  // Password dialogs
  const [selfPw, setSelfPw] = useState(null);   // { current, next, confirm } | null
  const [resetFor, setResetFor] = useState(null); // user being reset | null
  const [pwNext, setPwNext] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const canReset = me?.role === "super_admin" || me?.role === "tenant_admin";

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => toast.error("Failed to load users"));
  useEffect(() => { load(); }, []);

  const submitSelfPw = async () => {
    if ((selfPw?.next || "").length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (selfPw.next !== selfPw.confirm) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: selfPw.current, new_password: selfPw.next });
      if (data?.token) setToken(data.token);
      toast.success("Your password was changed");
      setSelfPw(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    }
    setPwBusy(false);
  };

  const submitReset = async () => {
    if (pwNext.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pwNext !== pwConfirm) { toast.error("Passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, { new_password: pwNext });
      toast.success(`Password reset for ${resetFor.name}`);
      setResetFor(null); setPwNext(""); setPwConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
    setPwBusy(false);
  };

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/users", form);
      toast.success(`Invited ${form.name} as ${ROLE_META[form.role]?.label || form.role}`);
      setForm({ name: "", email: "", password: "", role: "operator" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setCreating(false);
  };

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    setBusyId(u.id);
    // Optimistic UI
    setUsers((p) => p.map((x) => x.id === u.id ? { ...x, role: newRole } : x));
    try {
      await api.patch(`/users/${u.id}`, { role: newRole });
      toast.success(`${u.name} is now ${ROLE_META[newRole]?.label || newRole}`);
    } catch (e) {
      // Rollback on failure
      setUsers((p) => p.map((x) => x.id === u.id ? { ...x, role: u.role } : x));
      toast.error(e?.response?.data?.detail || "Role change failed");
    }
    setBusyId(null);
  };

  const del = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const rolesForSelect = (u) => {
    // Never let a tenant admin promote to super_admin. Super admins see the full list.
    const base = me?.role === "super_admin"
      ? ["operator", "scheduler", "tenant_admin", "super_admin"]
      : ["operator", "scheduler", "tenant_admin"];
    // Always include the user's current role (in case it's outside the set the actor can assign)
    if (!base.includes(u.role)) base.push(u.role);
    return base;
  };

  return (
    <div className="p-6 space-y-6" data-testid="users-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">§ Users</div>
          <h1 className="font-display font-black text-3xl mt-1 tracking-tight">Team & access</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Assign a role to control what each team member can do. Hover a role pill to see the exact permissions.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-sm shrink-0"
          onClick={() => setSelfPw({ current: "", next: "", confirm: "" })}
          data-testid="change-my-password-btn"
        >
          <KeyRound className="w-4 h-4 mr-2" /> Change my password
        </Button>
      </div>

      <div className="grid md:grid-cols-[1fr_360px] gap-4">
        <div className="border border-border rounded-md bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="font-display font-bold">Members ({users.length})</div>
            <RoleLegend />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase font-mono text-muted-foreground border-b border-border">
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2 min-w-[200px]">Role</th>
                  <th className="px-3 py-2 w-20" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const meta = ROLE_META[u.role] || ROLE_META.operator;
                  const isMe = me?.id === u.id;
                  return (
                    <tr key={u.id} className="border-b border-border hover:bg-muted/40" data-testid={`user-row-${u.id}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-sm flex items-center justify-center text-[10px] font-bold text-white" style={{ background: u.avatar_color }}>
                            {(u.name || "").split(" ").slice(0, 2).map((s) => s[0]).join("")}
                          </div>
                          <div>
                            <div>{u.name}</div>
                            {isMe && <div className="text-[9px] font-mono uppercase text-fuchsia-400">You</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{u.email}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Select
                            value={u.role}
                            onValueChange={(v) => changeRole(u, v)}
                            disabled={isMe || busyId === u.id}
                          >
                            <SelectTrigger
                              className={`h-8 w-[170px] rounded-sm text-xs ${meta.color} border`}
                              data-testid={`user-role-select-${u.id}`}
                              title={isMe ? "You cannot change your own role" : meta.blurb}
                            >
                              <div className="flex items-center gap-1.5">
                                <meta.icon className="w-3 h-3" />
                                <span>{meta.label}</span>
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              {rolesForSelect(u).map((r) => {
                                const rm = ROLE_META[r];
                                return (
                                  <SelectItem key={r} value={r} data-testid={`user-role-opt-${u.id}-${r}`}>
                                    <div className="flex items-center gap-2">
                                      <rm.icon className="w-3.5 h-3.5" />
                                      <div>
                                        <div>{rm.label}</div>
                                        <div className="text-[10px] text-muted-foreground max-w-[240px]">{rm.blurb}</div>
                                      </div>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          {busyId === u.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2 justify-end">
                          {canReset && (
                            <button
                              onClick={() => { setResetFor(u); setPwNext(""); setPwConfirm(""); }}
                              className="text-muted-foreground hover:text-cyan-400"
                              data-testid={`user-reset-pw-${u.id}`}
                              title={isMe ? "Reset your password" : `Reset password for ${u.name}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </button>
                          )}
                          {!isMe && (
                            <button
                              onClick={() => del(u)}
                              className="text-muted-foreground hover:text-rose-500"
                              data-testid={`user-del-${u.id}`}
                              title="Remove user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border border-border rounded-md bg-card h-fit">
          <div className="px-4 py-3 border-b border-border font-display font-bold">Invite member</div>
          <div className="p-4 space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 rounded-sm" data-testid="user-form-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1 rounded-sm" data-testid="user-form-email" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="mt-1 rounded-sm" data-testid="user-form-password" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="user-form-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CREATE_ROLES.map((r) => {
                    const rm = ROLE_META[r];
                    return (
                      <SelectItem key={r} value={r}>
                        <div className="flex items-center gap-2">
                          <rm.icon className="w-3.5 h-3.5" />
                          <div>
                            <div>{rm.label}</div>
                            <div className="text-[10px] text-muted-foreground max-w-[240px]">{rm.blurb}</div>
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={create} disabled={creating} className="w-full tenant-btn rounded-sm" data-testid="user-create-btn">
              {creating
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Plus className="w-4 h-4 mr-2" />}
              Add member
            </Button>
          </div>
        </div>
      </div>

      {/* Change my own password */}
      <Dialog open={!!selfPw} onOpenChange={(o) => { if (!o) setSelfPw(null); }}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change my password</DialogTitle>
            <DialogDescription>Enter your current password, then choose a new one (at least 8 characters).</DialogDescription>
          </DialogHeader>
          {selfPw && (
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Current password</Label>
                <Input type="password" value={selfPw.current} onChange={(e) => setSelfPw((s) => ({ ...s, current: e.target.value }))} className="mt-1 rounded-sm" data-testid="selfpw-current" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">New password</Label>
                <Input type="password" value={selfPw.next} onChange={(e) => setSelfPw((s) => ({ ...s, next: e.target.value }))} className="mt-1 rounded-sm" data-testid="selfpw-next" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Confirm new password</Label>
                <Input type="password" value={selfPw.confirm} onChange={(e) => setSelfPw((s) => ({ ...s, confirm: e.target.value }))} className="mt-1 rounded-sm" data-testid="selfpw-confirm" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelfPw(null)} data-testid="selfpw-cancel">Cancel</Button>
            <Button onClick={submitSelfPw} disabled={pwBusy} className="tenant-btn rounded-sm" data-testid="selfpw-submit">
              {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin resets another user's password */}
      <Dialog open={!!resetFor} onOpenChange={(o) => { if (!o) { setResetFor(null); setPwNext(""); setPwConfirm(""); } }}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password{resetFor ? ` · ${resetFor.name}` : ""}</DialogTitle>
            <DialogDescription>
              {resetFor ? `Set a new password for ${resetFor.email}. They'll need to sign in with it.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">New password</Label>
              <Input type="password" value={pwNext} onChange={(e) => setPwNext(e.target.value)} className="mt-1 rounded-sm" data-testid="resetpw-next" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Confirm new password</Label>
              <Input type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} className="mt-1 rounded-sm" data-testid="resetpw-confirm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setResetFor(null); setPwNext(""); setPwConfirm(""); }} data-testid="resetpw-cancel">Cancel</Button>
            <Button onClick={submitReset} disabled={pwBusy} className="tenant-btn rounded-sm" data-testid="resetpw-submit">
              {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RoleLegend() {
  return (
    <div className="hidden lg:flex items-center gap-1.5">
      {Object.entries(ROLE_META).filter(([k]) => k !== "super_admin").map(([k, m]) => (
        <Badge
          key={k}
          className={`${m.color} text-[9px] uppercase font-mono flex items-center gap-1 border`}
          title={m.blurb}
        >
          <m.icon className="w-2.5 h-2.5" /> {m.label}
        </Badge>
      ))}
    </div>
  );
}
